import { fmtDate, vehicleLabel } from "@/lib/format";

function escHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

export default function buildChallanHtml({ transfer, from_branch, to_branch, vehicles, dealership }) {
  const t = transfer || {};
  const d = dealership || {};
  const id = (t.id || "").slice(0, 8).toUpperCase();
  const dateStr = fmtDate(t.created_at);
  const status = (t.status || "").toUpperCase();

  const branchBlock = (label, b) => {
    const bb = b || {};
    return `<div class="addr">
      <div class="addr-label">${escHtml(label)}</div>
      <div class="addr-name">${escHtml(bb.name || "—")}</div>
      ${bb.code ? `<div class="muted">Code: ${escHtml(bb.code)}</div>` : ""}
      ${bb.address ? `<div>${escHtml(bb.address)}</div>` : ""}
      ${bb.gstin ? `<div>GSTIN: <b>${escHtml(bb.gstin)}</b></div>` : ""}
      ${bb.phone ? `<div>Ph: ${escHtml(bb.phone)}</div>` : ""}
      ${bb.manager ? `<div>Manager: ${escHtml(bb.manager)}</div>` : ""}
    </div>`;
  };

  const fmtAmt = (n) => {
    const v = Number(n) || 0;
    return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Per-vehicle row: prefer the **package name** for the description column
  // (operators recognise "JUPITER 125 BSVI-DISC" much faster than the dropped
  // make+model). Falls back to model_category for orphaned rows.
  const vehDescription = (v) => {
    const pkgNames = (v.package_names || []).filter(Boolean);
    if (pkgNames.length) return pkgNames.join(" · ");
    return vehicleLabel(v) || "—";
  };

  const rows = (vehicles || []).map((v, i) => `<tr>
    <td class="num">${i + 1}</td>
    <td>${escHtml(vehDescription(v))}</td>
    <td class="mono">${escHtml(v.vin || "—")}</td>
    <td class="mono">${escHtml(v.engine_no || "—")}</td>
    <td>${escHtml(v.color || "—")}</td>
    <td class="mono">${escHtml(v.hsn_code || "—")}</td>
    <td class="num">${escHtml(fmtAmt(v.price))}</td>
  </tr>`).join("");
  // Fallback: if the full vehicle docs aren't available (old/removed records),
  // render the per-transfer snapshot so the challan still has a manifest.
  const fallbackRows = !rows
    ? (t.vehicle_summaries || []).map((v, i) => `<tr>
        <td class="num">${i + 1}</td>
        <td>${escHtml(v.color || v.year || "")}</td>
        <td class="mono">${escHtml(v.vin || "—")}</td>
        <td class="mono">${escHtml(v.engine_no || "—")}</td>
        <td>${escHtml(v.color || "—")}</td>
        <td>—</td>
        <td class="num">—</td>
      </tr>`).join("")
    : "";
  const finalRows = rows || fallbackRows || `<tr><td colspan="8" style="text-align:center;color:#888;padding:20px">No vehicles in this transfer.</td></tr>`;
  const totalUnits = (vehicles || []).length || (t.vehicle_summaries || []).length;
  const totalPrice = (vehicles || []).reduce((s, v) => s + (Number(v.price) || 0), 0);

  return `<!doctype html><html><head><title>Delivery Challan · ${escHtml(id)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Times New Roman',serif;color:#111;margin:24px;background:#fff;max-width:820px}
  .hdr{display:flex;align-items:flex-start;gap:20px;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:10px}
  .logo{max-height:64px;max-width:160px;object-fit:contain}
  .dealer{flex:1}
  .dealer b{font-size:16px}
  .title{flex:1;text-align:right}
  .title h1{margin:0;font-size:22px;letter-spacing:3px}
  .title .sub{font-family:'IBM Plex Mono',monospace;font-size:10px;color:#555;text-transform:uppercase;letter-spacing:.18em;margin-top:4px}
  .meta-row{display:flex;justify-content:space-between;font-size:11.5px;margin:8px 0;padding:6px 0;border-bottom:1px dashed #ccc}
  .meta-row b{font-family:'Courier New',monospace}
  .addrs{display:grid;grid-template-columns:1fr 24px 1fr;gap:0;margin:14px 0;align-items:stretch}
  .addr{border:1px solid #000;padding:10px;font-size:11.5px;line-height:1.4}
  .addr-label{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:#666;margin-bottom:6px}
  .addr-name{font-size:13px;font-weight:700;margin-bottom:4px}
  .arrow{display:flex;align-items:center;justify-content:center;font-size:22px;color:#000}
  .muted{color:#888;font-weight:400;font-size:11px}
  table.veh{width:100%;border-collapse:collapse;margin-top:10px}
  table.veh th,table.veh td{padding:7px 9px;border-bottom:1px solid #eee;font-size:11.5px;text-align:left;vertical-align:top}
  table.veh th{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#444;border-bottom:2px solid #000;background:#fafafa}
  td.num,th.num{text-align:right;font-family:'Courier New',monospace}
  .mono{font-family:'Courier New',monospace;font-size:11px;letter-spacing:.5px}
  .note{margin-top:10px;padding:8px 10px;background:#fafafa;border:1px solid #ddd;font-size:11.5px;font-style:italic}
  .declaration{margin-top:18px;padding:10px;border:1px solid #000;font-size:11px;line-height:1.5}
  .signatures{display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;margin-top:50px;font-size:11px}
  .sig{text-align:center}
  .sig-line{border-bottom:1px solid #000;height:30px;margin-bottom:4px}
  .sig-label{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.18em;text-transform:uppercase;color:#666}
  .foot{margin-top:30px;font-family:'IBM Plex Mono',monospace;font-size:9px;color:#888;text-transform:uppercase;letter-spacing:.18em;text-align:center}
  .stamp{display:inline-block;padding:2px 8px;border:1.5px solid;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.18em;text-transform:uppercase}
  .stamp.pending{color:#B58900;border-color:#B58900}
  .stamp.approved{color:#00713a;border-color:#00713a}
  .stamp.rejected{color:#a30000;border-color:#a30000}
  .stamp.cancelled{color:#666;border-color:#666}
  @media print{button{display:none}}
</style></head><body>
<div class="hdr">
  ${d.logo_url ? `<img class="logo" src="${escHtml(d.logo_url)}" alt="logo"/>` : ""}
  <div class="dealer">
    <b>${escHtml(d.name || "DEALERSHIP")}</b><br/>
    ${d.address ? `<span style="font-size:11px">${escHtml(d.address)}</span><br/>` : ""}
    ${d.state_name ? `<span style="font-size:11px">State: ${escHtml(d.state_name || "—")} · Code: ${escHtml(d.state_code || "—")}</span><br/>` : ""}
    ${d.gstin ? `<span style="font-size:11px">GSTIN: <b>${escHtml(d.gstin)}</b></span>` : ""}
  </div>
  <div class="title">
    <h1>DELIVERY CHALLAN</h1>
    <div class="sub">Inter-Branch Stock Transfer · Not For Sale</div>
  </div>
</div>

<div class="meta-row">
  <div>Challan No: <b>DC-${escHtml(id)}</b></div>
  <div>Date: <b>${escHtml(dateStr)}</b></div>
  <div>Status: <span class="stamp ${escHtml((t.status || "").toLowerCase())}">${escHtml(status || "PENDING")}</span></div>
</div>

<div class="addrs">
  ${branchBlock("Consignor (From)", from_branch)}
  <div class="arrow">→</div>
  ${branchBlock("Consignee (To)", to_branch)}
</div>

<div style="font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#444;margin-top:14px;margin-bottom:4px">
  Vehicles in transit · ${totalUnits} unit${totalUnits === 1 ? "" : "s"}
</div>
<table class="veh">
  <thead><tr>
    <th class="num">SN</th>
    <th>Description</th>
    <th>VIN / Chassis No</th>
    <th>Engine No</th>
    <th>Color</th>
    <th>HSN</th>
    <th class="num">Purchase Price (₹)</th>
  </tr></thead>
  <tbody>${finalRows}</tbody>
  ${rows ? `<tfoot><tr>
    <td colspan="6" style="text-align:right;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;border-top:2px solid #000;padding:8px 9px">Total</td>
    <td class="num" style="border-top:2px solid #000;padding:8px 9px;font-weight:700">₹${escHtml(fmtAmt(totalPrice))}</td>
  </tr></tfoot>` : ""}
</table>

${t.note ? `<div class="note">Note: ${escHtml(t.note)}</div>` : ""}
${t.decision_reason ? `<div class="note">Decision reason: ${escHtml(t.decision_reason)}</div>` : ""}

<div class="declaration">
  <b>Declaration:</b> The above-listed vehicle(s) are being transferred from the
  consignor branch to the consignee branch for stock relocation purposes only.
  This is <b>not a sale</b>; no GST is being charged on this movement. The
  vehicles are dispatched in good condition and the consignee acknowledges
  receipt at delivery.
</div>

<div class="signatures">
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">Dispatched By (Consignor)</div>
    ${t.by_user_name ? `<div style="margin-top:2px"><b>${escHtml(t.by_user_name)}</b></div>` : ""}
  </div>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">Driver / Transporter</div>
  </div>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">Received By (Consignee)</div>
    ${t.decided_by_user_name && t.status === "approved" ? `<div style="margin-top:2px"><b>${escHtml(t.decided_by_user_name)}</b></div>` : ""}
  </div>
</div>

<div class="foot">Computer-generated delivery challan · ${escHtml(new Date().toLocaleString())}</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;
}
